import { PermissionKey, PostStatus, UserRole } from '../enums';
import { DomainError } from '../errors/domain-error';
import { hasPermission } from '../rbac';

export function assertCreatorProfileRequired(creator: unknown): asserts creator is NonNullable<typeof creator> {
  if (!creator) {
    throw new DomainError('Creator profile required');
  }
}

export function authorizePostDeletion(params: {
  role: UserRole;
  userId: string;
  postAuthorId: string;
}): { isOwner: boolean; isModeratorAction: boolean } {
  const isOwner = params.userId === params.postAuthorId;

  if (isOwner && hasPermission(params.role, PermissionKey.CAN_DELETE_POST)) {
    return { isOwner: true, isModeratorAction: false };
  }

  if (hasPermission(params.role, PermissionKey.CAN_MODERATE_CONTENT)) {
    return { isOwner, isModeratorAction: !isOwner };
  }

  throw new DomainError('Cannot delete this post');
}

export function authorizeMemberPostCreation(role: UserRole): void {
  if (
    !hasPermission(role, PermissionKey.CAN_CREATE_MEMBER_POST) &&
    !hasPermission(role, PermissionKey.CAN_CREATE_POST)
  ) {
    throw new DomainError('Cannot create posts');
  }
}

export function authorizeArticleCreation(role: UserRole): void {
  if (!hasPermission(role, PermissionKey.CAN_CREATE_ARTICLE)) {
    throw new DomainError('Cannot create articles');
  }
}

/** Rough read-time estimate: ~200 words per minute, minimum 1. */
export function estimateArticleReadTimeMinutes(bodyText: string): number {
  const words = bodyText.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

export type ArticlePublishValidation =
  | { ok: true }
  | { ok: false; message: string };

/** Shared client/API guard before publishing an article draft. */
export function validateArticlePublishRequirements(
  title: string,
  bodyText: string,
): ArticlePublishValidation {
  if (!title.trim()) {
    return { ok: false, message: 'Add a title before publishing' };
  }
  if (!bodyText.trim()) {
    return { ok: false, message: 'Add article content before publishing' };
  }
  return { ok: true };
}

export function slugifyArticleTitle(title: string): string {
  const base = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
  return base || 'article';
}

export function isPostVisibleInPublicFeed(status: PostStatus | string): boolean {
  return status === PostStatus.PUBLISHED;
}

export function defaultPostStatus(requested?: PostStatus): PostStatus {
  return requested ?? PostStatus.DRAFT;
}

export function validatePlatformPostSchedule(
  scheduledFor?: Date | string | null,
  now: Date = new Date(),
): { ok: true; value: Date } | { ok: false; message: string } {
  if (!scheduledFor) {
    return { ok: false, message: 'Schedule time is required' };
  }
  const date = scheduledFor instanceof Date ? scheduledFor : new Date(scheduledFor);
  if (Number.isNaN(date.getTime())) {
    return { ok: false, message: 'Invalid schedule time' };
  }
  if (date.getTime() <= now.getTime()) {
    return { ok: false, message: 'Schedule time must be in the future' };
  }
  return { ok: true, value: date };
}

export function resolvePlatformPostCreateStatus(params: {
  status?: PostStatus;
  scheduledFor?: Date | null;
}): PostStatus {
  if (params.scheduledFor) return PostStatus.DRAFT;
  return defaultPostStatus(params.status);
}

export function effectivePlatformPostSchedule(params: {
  nextScheduledFor?: Date | null;
  currentScheduledFor?: Date | null;
}): Date | null {
  if (params.nextScheduledFor !== undefined) {
    return params.nextScheduledFor;
  }
  return params.currentScheduledFor ?? null;
}

export function isPlatformPostSchedulePending(
  scheduledFor: Date | null | undefined,
  now: Date = new Date(),
): boolean {
  return Boolean(scheduledFor && scheduledFor.getTime() > now.getTime());
}

export function resolvePlatformPostUpdateStatus(params: {
  requestedStatus?: PostStatus;
  nextScheduledFor?: Date | null;
  currentScheduledFor?: Date | null;
  now?: Date;
}): PostStatus | undefined {
  const now = params.now ?? new Date();
  const schedule = effectivePlatformPostSchedule({
    nextScheduledFor: params.nextScheduledFor,
    currentScheduledFor: params.currentScheduledFor,
  });

  if (isPlatformPostSchedulePending(schedule, now)) {
    return PostStatus.DRAFT;
  }

  return params.requestedStatus;
}

export function assertPlatformPostManualPublishAllowed(params: {
  scheduledFor?: Date | null;
  now?: Date;
}): { ok: true } | { ok: false; message: string } {
  if (isPlatformPostSchedulePending(params.scheduledFor, params.now)) {
    return {
      ok: false,
      message: 'Scheduled posts publish automatically at the scheduled time',
    };
  }
  return { ok: true };
}
